import React, { useEffect, useState, useRef, useCallback, useContext, useMemo } from 'react';
import { array, number, oneOfType, func, shape, any, bool } from 'prop-types';
import { useTable, useRowSelect, useFilters, useFlexLayout } from 'react-table';
import { connect } from 'react-redux';
import { FixedSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { CheckboxStyled, TextStyled, useTokens } from '@nike/nike-design-system-components';
import { Box } from 'rebass';

import useWindowSize from '../../hooks/useWindowSize';
import { usePrevious } from '../../hooks/usePrevious';

import { scrollbarWidth } from './helpers';

import { saveTableState } from './actions';

import Spinner from '../spinner/Spinner';

import { TableServerSideContext } from './TableServerSideContext';
import { Icon } from '../index';
import { StickyButton } from './styles';

const defaultColumn = {
  minWidth: 50,
  width: 150
};

/* eslint-disable */

const isOverflown = element => {
  return element?.scrollHeight > element?.clientHeight || element?.scrollWidth > element?.clientWidth;
};

/*
 Infinite-scroll table rendered by TableServerSide.
 Based on this example: https://www.npmjs.com/package/react-window-infinite-loader
*/
const TableInfiniteScroll = ({
  fullHeight,
  hasNextPage, // Are there more items to load? (This information comes from the most recent API request.)
  isNextPageLoading, // Are we currently loading a page of items?
  items, // Array of items loaded so far.
  itemSize,
  loadNextPage, // Callback function responsible for loading the next page of items.
  columns,
  includeRowSelect, // Whether to include a column for selecting rows
  containerRef,
  height,
  saveTableState,
  infiniteLoaderRef
}) => {
  const { setTableInstance, savedTableState, columnSortModifiers, columnFilterModifiers, tableId } =
    useContext(TableServerSideContext);

  const { pageWidth } = useWindowSize();

  // ensure scrollbar size is updated if screen is resized
  const scrollbarSize = useMemo(scrollbarWidth, [pageWidth]);

  const listOuterRef = useRef(null);
  const innerListRef = useRef(undefined);

  const tableInfiniteScrollRef = useRef(null);

  const tokens = useTokens();

  const { sortBy: savedSortBy, filters: savedFilters } = savedTableState;

  const [sortBy, setSortBy] = useState(savedSortBy || columnSortModifiers.defaultSort || {});

  const [hasReachedEndOfScroll, setHasReachedEndOfScroll] = useState(false);

  const heightToBottomOfWindow = window.innerHeight - (tableInfiniteScrollRef?.current?.offsetTop + 55 + scrollbarSize);

  const previousIsNextPageLoading = usePrevious(isNextPageLoading);

  const [scrollOffsetX, setScrollOffsetX] = useState(0);
  const [scrollOffsetY, setScrollOffsetY] = useState(0);
  const scrollOffsetXRef = useRef(scrollOffsetX);
  const scrollOffsetYRef = useRef(scrollOffsetY);

  const updateScrollOffset = (offset, dir) => {
    if (dir === 'Y') {
      scrollOffsetYRef.current = offset;
      setScrollOffsetY(offset);
    } else if (dir === 'X') {
      scrollOffsetXRef.current = offset;
      setScrollOffsetX(offset);
    }
  };

  const itemOffset = 70;
  const minHeight = 0.1;

  const navToOffset = () => {
    listOuterRef.current?.scrollTo?.({
      left: 0,
      top: scrollOffsetYRef.current,
      behavior: 'auto'
    });

    if (containerRef.current && scrollOffsetXRef.current)
      containerRef.current.scrollTo?.({
        left: scrollOffsetXRef.current,
        top: scrollOffsetYRef.current,
        behavior: 'auto'
      });
  };

  const handleKeyDown = ({ keyCode }) => {
    const { scrollTop } = listOuterRef.current ?? {};
    const { scrollLeft } = containerRef.current ?? {};

    const [pageUp, pageDown, pageLeft, pageRight] = [38, 40, 37, 39];
    const keys = {
      [pageUp]: Math.max(minHeight, scrollTop - itemOffset),
      [pageDown]: scrollTop + itemOffset,
      [pageLeft]: Math.max(minHeight, scrollLeft - itemOffset),
      [pageRight]: scrollLeft + itemOffset
    };

    if (keys[keyCode]) {
      if ([pageUp, pageDown].includes(keyCode)) updateScrollOffset(keys[keyCode], 'Y');
      if ([pageLeft, pageRight].includes(keyCode)) updateScrollOffset(keys[keyCode], 'X');

      navToOffset();
    }
  };

  const handleScroll = () => {
    navToOffset();
  };

  useEffect(() => {
    document.body.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return function cleanup() {
      document.body.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const reset = () => {
    updateScrollOffset(0, 'X');
    updateScrollOffset(0, 'Y');

    listOuterRef.current?.scrollTo?.({
      left: 0,
      top: 0,
      behavior: 'auto'
    });

    containerRef.current?.scrollTo?.({
      left: 0,
      top: 0,
      behavior: 'auto'
    });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: tableState,
    setAllFilters,
    isAllRowsSelected
  } = useTable(
    {
      columns,
      data: items,
      defaultColumn,
      manualFilters: true,
      manualPagination: true,
      initialState: {
        filters: savedFilters || columnFilterModifiers?.defaultFilters || []
      }
    },
    useFlexLayout,
    useFilters,
    useRowSelect,
    hooks => {
      if (!includeRowSelect) return;

      hooks.visibleColumns.push(columns => [
        {
          width: 75,
          maxWidth: -1,
          id: 'selection',
          // eslint-disable-next-line react/prop-types
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <CheckboxStyled {...getToggleAllRowsSelectedProps()} indeterminate='false' />
          ),
          // eslint-disable-next-line react/prop-types
          Cell: ({ row }) => <CheckboxStyled {...row.getToggleRowSelectedProps()} indeterminate='false' />
        },
        ...columns
      ]);
    }
  );

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = hasNextPage ? items.length + 1 : items.length;

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadMoreItems = isNextPageLoading ? () => {} : loadNextPage;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = index => !hasNextPage || index < items.length;

  const RenderRow = useCallback(
    ({ index, style }) => {
      const row = rows[index];

      if (row) {
        prepareRow(row);

        return (
          <div
            key={row.original?.id}
            {...row.getRowProps({
              style
            })}
            className='tr'
          >
            {row.cells.map(cell => {
              const { key } = cell.getCellProps();

              return (
                <div
                  key={key}
                  {...cell.getCellProps({
                    style: {
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom:
                        !hasNextPage && index === rows.length - 1
                          ? `1px solid ${tokens.podiumCdsColorBorderSecondary}`
                          : '1px solid inherit'
                    }
                  })}
                  className='td'
                >
                  {cell.render('Cell')}
                </div>
              );
            })}
          </div>
        );
      }

      return <Spinner large />;
    },
    [prepareRow, rows.length, hasNextPage, isAllRowsSelected]
  );

  // Show scroll if loading more rows
  useEffect(() => {
    if (!previousIsNextPageLoading && isNextPageLoading) {
      setHasReachedEndOfScroll(false);
    } else {
      setHasReachedEndOfScroll(true);
    }
  }, [previousIsNextPageLoading, isNextPageLoading]);

  useEffect(() => {
    setTableInstance({ isAllRowsSelected, ...tableState, selectedFlatRows, sortBy, setSortBy, setAllFilters, items });

    saveTableState({ tableId, tableState: { ...tableState, sortBy, selectedFlatRows } });
  }, [JSON.stringify(tableState), selectedFlatRows.length, JSON.stringify(sortBy), tableId, JSON.stringify(items)]);

  return (
    <div {...getTableProps()} className='table-infinite-scroll' ref={tableInfiniteScrollRef}>
      <div>
        {headerGroups.map(headerGroup => (
          <div {...headerGroup.getHeaderGroupProps()} className='tr'>
            {headerGroup.headers.map(column => (
              <div {...column.getHeaderProps()} className='th'>
                {column.render('Header', { sortBy, setSortBy })}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div
        {...getTableBodyProps()}
        className='tbody'
        style={{ height: `${fullHeight ? heightToBottomOfWindow : height}px` }}
      >
        {itemCount === 0 && !isNextPageLoading && (
          <Box p='15px' style={{ position: 'absolute' }}>
            <TextStyled appearance='body2'>No Data Available.</TextStyled>
          </Box>
        )}
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          ref={infiniteLoaderRef}
        >
          {({ onItemsRendered, ref }) => (
            <AutoSizer>
              {({ height, width }) => (
                <List
                  outerRef={listOuterRef}
                  innerRef={innerListRef}
                  height={height}
                  // only include scrollbar in width if there is one
                  width={isOverflown(listOuterRef.current) ? width + scrollbarSize : width}
                  itemCount={itemCount}
                  itemSize={itemSize}
                  onItemsRendered={onItemsRendered}
                  selectedRows={tableState?.selectedRowIds} // force re-render when selectedRows changes
                  ref={ref}
                >
                  {RenderRow}
                </List>
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>

        <StickyButton type='button' onClick={reset}>
          <Icon name='arrow-up' isLight alt='Arrow Up Icon' />
        </StickyButton>

        {isNextPageLoading && items.length > 0 && hasReachedEndOfScroll && (
          <Box style={{ position: 'absolute', left: '50%', bottom: '30px' }}>
            <Spinner small />
          </Box>
        )}
      </div>
    </div>
  );
};

TableInfiniteScroll.propTypes = {
  columns: array.isRequired,
  fullHeight: bool,
  hasNextPage: bool,
  includeRowSelect: bool,
  containerRef: oneOfType([func, shape({ current: any })]),
  isNextPageLoading: bool,
  items: array.isRequired,
  itemSize: number,
  loadNextPage: func.isRequired,
  height: number,
  saveTableState: func.isRequired,
  infiniteLoaderRef: oneOfType([func, shape({ current: any })])
};

TableInfiniteScroll.defaultProps = {
  fullHeight: false,
  hasNextPage: false,
  includeRowSelect: false,
  containerRef: () => {},
  isNextPageLoading: false,
  itemSize: 70,
  height: 500,
  infiniteLoaderRef: () => {}
};

export default connect(null, { saveTableState })(TableInfiniteScroll);
